import { createSlice } from '@reduxjs/toolkit';
//
import { uniqBy } from 'lodash';
import moment from 'moment';
import { practiceInSuffix } from 'src/utils/practiceInSuffix';
import { dispatch, store } from '../store';
// slice
import { updateError } from './user';
// utils
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';

// All Practices phase-2: pull the selection off the store each call so
// campaign list endpoints aggregate when the user has multi-practice mode on.
const _activePracticesSuffix = () =>
    practiceInSuffix(store.getState().practiceTreatmentList?.activePracticeIds);

// -----------------------------------------------

const initialState = {
    isLoading: false,
    isLoadingFinish: false,
    loadMoreLoading: false,
    campaignsData: null,
    newCampaign: {
        campaignName: null,
        engagement_template_tab: 'All',
        audienceSelected: null
    },
    selectedCampaign: {},
    conditionList: [],
    markAsComplete: false,
    campaignReport: null,
    campaignTimeline: null, // { count, results: [{id, recipient, status, timestamp, error, lead_pk}] }
    campaignTimelineLoading: false,
    campaignLeadData: {
        results: []
    },
    campaignData: {},
    campaignCounter: [],
    fileUploadError: '',
    finalCampaignData: {},
    isIncludeArchive: false,
    mediaImages: {
        results: [],
        count: 0
    },
    basicBody: '',
    tempcampaignData: {},
    currentActiveTab: 'Existing audience',
    usedIn: '',
    loading: false,
    whatsAppTemplateLoading: false,
    whatsAppTemplate: {},
    allWhatsappTemplates: [],
    allWhatsappTemplateLoading: false,
    campaignDetail: null,
    campaignDetailLoading: false
};

const slice = createSlice({
    name: 'campaigns',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoadingData(state, action) {
            state.isLoadingFinish = action.payload;
        },
        clearNewCampaignData(state, action) {
            state.markAsComplete = false;
            if (!action.payload?.isPreview) {
                state.selectedCampaign = {};
                state.newCampaign = {
                    campaignName: null,
                    engagement_template_tab: 'All'
                };
            }
            state.conditionList = [];
            state.finalCampaignData = {};
            state.isIncludeArchive = false;
        },
        // START LOADING
        startLoading(state) {
            state.isLoading = true;
        },
        setCampaignListing(state, action) {
            state.isLoading = false;
            state.campaignsData = action.payload;
        },
        saveNewCampaign(state, action) {
            state.isLoading = false;
            state.newCampaign = {
                ...state.newCampaign,
                ...action.payload
            };
        },
        updateCampaignStep(state, action) {
            state.markAsComplete = action.payload;
        },
        updateSelectedCampaign(state, action) {
            state.selectedCampaign = action.payload;
        },
        updateConditionList(state, action) {
            state.conditionList = action.payload;
        },
        setCampaignReport(state, action) {
            state.isLoading = false;
            state.campaignReport = action.payload;
        },
        setCampaignTimeline(state, action) {
            state.campaignTimeline = action.payload;
            state.campaignTimelineLoading = false;
        },
        startCampaignTimelineLoading(state) {
            state.campaignTimelineLoading = true;
        },
        setCampaignLeadData(state, action) {
            state.isLoading = false;
            state.campaignLeadData = action.payload;
        },
        setCampaignData(state, action) {
            state.isLoading = false;
            state.campaignData = action.payload;
        },
        updateCounter(state, action) {
            state.campaignCounter = action.payload;
        },
        setMediaImages(state, action) {
            state.mediaImages.results = action.payload?.results;
            state.mediaImages.count = action.payload?.count;
        },
        clearMediaImages(state) {
            state.mediaImages.results = [];
            state.mediaImages.count = 0;
        },
        setLoadMoreLoading(state, action) {
            state.loadMoreLoading = action.payload;
        },
        setFileUploadError(state, action) {
            state.fileUploadError = action.payload;
        },
        setFinalCampaignData(state, action) {
            state.finalCampaignData = action.payload;
        },
        setIncludeArchive(state, action) {
            state.isIncludeArchive = action.payload;
        },
        setBasicBody(state, action) {
            state.basicBody = action.payload;
        },
        setTempcampaignData(state, action) {
            state.tempcampaignData = action.payload;
        },
        setCurrentTab(state, action) {
            state.currentActiveTab = action.payload;
        },
        setUsedIn(state, action) {
            state.usedIn = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setPreviewWhatsappStart(state, action) {
            state.whatsAppTemplateLoading = action.payload?.loading;
            state.whatsAppTemplate = action.payload?.template;
        },
        setAllWhatsappTemplateLoading(state, action) {
            state.allWhatsappTemplateLoading = action?.payload
        },
        setAllWhatsappTemplates(state, action) {
            state.allWhatsappTemplates = action?.payload;
        },
        setCampaignDetail(state, action) {
            state.campaignDetailLoading = false;
            state.campaignDetail = action.payload;
        },
        startCampaignDetailLoading(state) {
            state.campaignDetailLoading = true;
        }
    }
});

export default slice.reducer;

export const { setCampaignListing, reset, startLoading, saveNewCampaign, updateCampaignStep, updateSelectedCampaign, clearNewCampaignData, updateConditionList, setCampaignReport, startLoadingData, setCampaignLeadData, setCampaignData, updateCounter, setMediaImages, setLoadMoreLoading, setFileUploadError, clearMediaImages, setFinalCampaignData, setIncludeArchive, setBasicBody, setTempcampaignData, setCurrentTab, setUsedIn, setCampaignDetail, startCampaignDetailLoading, setCampaignTimeline, startCampaignTimelineLoading } = slice.actions;

const getCampaign = dt => {
    if (dt === "All Campaigns") return "";
    if (dt === "Whatsapp Campaigns") return "WHATSAPP";
    if (dt === "Email Campaigns") return "EMAIL";
    if (dt === "Sms Campaigns") return "SMS";
    return "";
}

export function getCampaignListing(data, handleClose) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const limit = data?.limit ? `&limit=${data?.limit}` : '';
            const campaignFilter = getCampaign(data?.campaignType) ? `&channel=${getCampaign(data?.campaignType)}` : "";
            const offset = (data?.offset === 0 || data?.offset) ? `&offset=${data?.offset}` : '';
            const status = data.status ? `&status__in=${data?.status}` : '';
            const searchQuery = data.searchQuery ? `&name__icontains=${data?.searchQuery}` : '';
            const param = `${limit}${offset}${status}${searchQuery}`;
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/?${campaignFilter}&ordering=-created_at${param}${_activePracticesSuffix()}`);
            if (response.status === 200) {
                dispatch(slice.actions.setCampaignListing(response.data));
                if (handleClose) {
                    handleClose(response.data.count);
                }
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}
export function getCampaignListingCounter(data, handleClose) {
    return async () => {
        try {
            const limit = data?.limit ? `&limit=${data?.limit}` : '';
            const offset = (data?.offset === 0 || data?.offset) ? `&offset=${data?.offset}` : '';
            const campaignFilter = getCampaign(data?.campaignType) ? `&channel=${getCampaign(data?.campaignType)}` : "";
            const status = data.status && data.status !== 'All' ? `&status__in=${data?.status}` : '';
            const searchQuery = data.searchQuery ? `&name__icontains=${data?.searchQuery}` : '';
            const param = `${limit}${offset}${status}${searchQuery}`;
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/?${campaignFilter}&ordering=-created_at${param}&fields={}${_activePracticesSuffix()}`);
            if (response.status === 200) {
                const { campaignCounter } = store.getState().campaign;
                dispatch(slice.actions.updateCounter([...campaignCounter, { filterStatus: data.status, count: response.data.count }]));
                if (handleClose) {
                    handleClose(response.data.count);
                }
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

// export function getCounterCampaign(data) {
//     return async () => {
//         try {
//             const limit = data?.limit ? `&limit=${data?.limit}` : '';
//             const offset = (data?.offset === 0 || data?.offset) ? `&offset=${data?.offset}` : '';
//             const status = data.status ? `&lead_treatments__status__in=${data?.status}` : '';
//             const search = `${limit}${offset}${status}`;
//             const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/?ordering=-created_at${search}&fields={}`);
//             if (response.status === 200) {
//                 const { counter } = store.getState().lead;
//             } else {
//                 dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
//             }
//         } catch (error) {
//             dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
//         }
//     }
// }

export function deleteCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/`, 'delete');
            if (response.status === 204) {
                handleClose();
                dispatch(updateError({ success: true, message: 'Campaign deleted successfully' || 'Something went wrong' }));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function createCampaign(data, handleClose, type) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Campaign created successfully' || 'Something went wrong' }));
                dispatch(updateSelectedCampaign(response.data));
                dispatch(slice.actions.setCampaignData(response.data));
                if (data?.audience) {
                    dispatch(startLoadingData(false));
                    dispatch(updateCampaignStep(false));
                }
                if (type === 'duplicateEvent') {
                    handleClose();
                }
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (data?.audience) {
                    dispatch(startLoadingData(false));
                    dispatch(updateCampaignStep(false));
                }
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function previewRecepientsCampaign(data, id, fieldParams) {
    return async () => {
        try {
            const limit = fieldParams?.limit ? `&limit=${fieldParams?.limit}` : '';
            const offset = (fieldParams?.offset === 0 || fieldParams?.offset) ? `&offset=${fieldParams?.offset}` : '';
            const param = `${limit}${offset}`;
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${id}/preview_recepients/?ordering=-created_at${param}`, 'post', data);
            if (response.status === 200) {
                dispatch(slice.actions.setCampaignLeadData(response.data));
                dispatch(startLoadingData(false));
                dispatch(updateCampaignStep(false));
            } else {
                dispatch(startLoadingData(false));
                dispatch(updateCampaignStep(false));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function updateCampaign(data, id, handleClose, handleSaveClose, sendCampaign, isTagUpdate) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${id}/`, 'put', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Campaign updated successfully' || 'Something went wrong' }));
                dispatch(startLoadingData(false));
                dispatch(updateCampaignStep(false));
                if (data?.audience) {
                    dispatch(startLoadingData(false));
                }
                if (handleSaveClose) {
                    handleClose(sendCampaign, response?.data?.id);
                } else if (isTagUpdate) {
                    handleClose('tag');
                } else {
                    handleClose(response?.data);
                }
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                dispatch(startLoadingData(false));
                dispatch(updateCampaignStep(false));
                // Call callback with null to indicate failure
                if (handleClose && typeof handleClose === 'function' && !handleSaveClose && !isTagUpdate) {
                    handleClose(null);
                }
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(startLoadingData(false));
            dispatch(updateCampaignStep(false));
            // Call callback with null to indicate failure
            if (handleClose && typeof handleClose === 'function' && !handleSaveClose && !isTagUpdate) {
                handleClose(null);
            }
        }
    };
}

export function cancelCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/cancel/`, 'post');
            if (response.status === 200) {
                handleClose();
                dispatch(updateError({ success: true, message: 'Campaign cancelled successfully' }));
            } else {
                const msg = response.data?.message || response.data?.exception || JSON.stringify(response.data) || 'Something went wrong';
                const isAlreadyCancelled = msg.includes('CANCELLED') || msg.includes('TransitionNotAllowed');
                dispatch(updateError({ success: isAlreadyCancelled, message: isAlreadyCancelled ? 'Campaign is already cancelled' : msg }));
                if (isAlreadyCancelled) handleClose();
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function scheduleCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/schedule/`, 'post');
            if (response.status === 200) {
                handleClose('schedule');
                dispatch(updateError({ success: true, message: 'Campaign scheduled successfully', id: data.id }));
            } else {
                const msg = response.data?.message || response.data?.exception || JSON.stringify(response.data) || 'Something went wrong';
                const isTransitionError = msg.includes('TransitionNotAllowed') || msg.includes('SCHEDULED');
                dispatch(updateError({ success: isTransitionError, message: isTransitionError ? 'Campaign is already scheduled' : msg }));
                if (isTransitionError) handleClose('schedule');
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getCampaignReport(data) {
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const [reportResponse, campaignResponse] = await Promise.all([
                callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data?.id}/report/`),
                callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data?.id}/`),
            ]);
            if (reportResponse.status === 200) {
                const reportList = reportResponse.data?.results || reportResponse.data || [];
                const campaign = campaignResponse.data || {};
                const channel = campaign.channel || 'EMAIL';

                // Transform list of {event/status, count} into flat object
                const reportData = { channel, campaignName: campaign.name || '' };
                if (Array.isArray(reportList)) {
                    reportList.forEach((item) => {
                        const key = item.event || item.status;
                        if (key) {
                            reportData[key] = (reportData[key] || 0) + (item.count || 0);
                        }
                    });
                }

                dispatch(slice.actions.setCampaignReport(reportData));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(reportResponse.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

/**
 * Fetch per-recipient send timeline for a campaign.
 *
 * Hits the new `/campaigns/{id}/timeline/` endpoint which returns paginated
 * ScheduledMessage rows ordered by activity DESC, each row carrying derived
 * channel-agnostic ``status`` (pending/sent/skipped/failed) + ``timestamp``
 * + ``error`` fields. Used by the CampaignLaunchSuccess live-progress view
 * and any future "drill into per-recipient detail" surface.
 *
 * Errors are NOT surfaced as toasts because this fires every 5s during
 * polling — a transient blip shouldn't spam the user. Silent on failure.
 */
export function getCampaignTimeline({ practiceId, id, statusFilter, limit = 25, offset = 0 }) {
    return async () => {
        if (!practiceId || !id) return;
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            if (limit) params.set('limit', String(limit));
            if (offset) params.set('offset', String(offset));
            const qs = params.toString() ? `?${params.toString()}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/campaigns/${id}/timeline/${qs}`);
            if (response.status === 200) {
                dispatch(slice.actions.setCampaignTimeline(response.data));
            }
            // Silent on non-200; the next 5s tick will retry.
        } catch (error) {
            // Silent — see docstring.
        }
    };
}

/**
 * Re-queue a single failed ScheduledMessage for re-send.
 *
 * Backs the per-row "Retry" button on the send timeline. The backend
 * resets sent_count + last_send_attempt_result on the SM row and fires
 * the send_message Celery task again. The next /timeline/ poll will
 * show the row drop back to 'pending' then settle to its new terminal
 * state.
 *
 * @param {{practiceId: number, id: number, smId: number}} params
 * @param {() => void} onDone fires after the API call resolves (success or fail)
 *                            so the UI can clear the row's "retrying" spinner.
 */
export function retryRecipient({ practiceId, id, smId }, onDone) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/campaigns/${id}/retry-recipient/${smId}/`,
                'post',
            );
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Retrying recipient…' }));
            } else {
                dispatch(updateError({ success: false, message: 'Retry failed' }));
            }
        } catch (_) {
            dispatch(updateError({ success: false, message: 'Retry failed' }));
        } finally {
            if (onDone) onDone();
        }
    };
}

/**
 * Re-queue every failed ScheduledMessage for a campaign at once.
 *
 * Backs the "Retry failed (N)" button on the report page header.
 * The backend re-enqueues all SMs with a non-Success / non-Skipped
 * last attempt result.
 */
export function retryFailedRecipients({ practiceId, id }, onDone) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/campaigns/${id}/retry-failed/`,
                'post',
            );
            if (response.status === 200) {
                const n = response.data?.retried ?? 0;
                dispatch(updateError({
                    success: n > 0,
                    message: n > 0
                        ? `Retrying ${n.toLocaleString()} recipient${n === 1 ? '' : 's'}…`
                        : 'No failed recipients to retry',
                }));
                if (onDone) onDone(n);
            } else {
                dispatch(updateError({ success: false, message: 'Retry failed' }));
                if (onDone) onDone(0);
            }
        } catch (_) {
            dispatch(updateError({ success: false, message: 'Retry failed' }));
            if (onDone) onDone(0);
        }
    };
}

export function sendEngagementTestMail({ practiceId, tempLateId, detail, callback }) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/engagement-templates/${tempLateId}/send_test/`, 'post', detail);
            // BE now executes test sends SYNCHRONOUSLY and reports back
            // ``sent`` (bool) + ``result`` (str). 200 = success; 502 =
            // queued but actual provider delivery failed — surface the
            // real result so operators don't get a green snackbar for a
            // silently-dropped send.
            const sent = response?.data?.sent;
            const result = response?.data?.result;
            if (response.status === 200 && sent === true) {
                callback({ success: true, message: 'Test email sent successfully' });
            } else if (sent === false) {
                callback({
                    success: false,
                    message: `Test send failed: ${result || 'unknown error'}`,
                });
            } else {
                callback({ success: false, message: response.data });
            }
        } catch (error) {
            dispatch(updateError(error || 'Something want wrong'));
        }
    };
}

export function getMediaImages({ practiceId, shouldReset, limit = 50, offset = 0 }) {
    return async () => {
        try {
            dispatch(setLoadMoreLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/media/?offset=${offset}&limit=${limit}&ordering=-created_at`);
            const { mediaImages } = store.getState().campaign;
            if (response.status === 200) {
                if (shouldReset) {
                    dispatch(slice.actions.setMediaImages({ results: response.data.results, count: response.data?.count }));
                    dispatch(setLoadMoreLoading(false));
                    return;
                }
                dispatch(setLoadMoreLoading(false));
                const images = [...mediaImages.results, ...response.data.results];
                const imagesList = uniqBy(images, 'id');
                const sortedImages = imagesList?.sort((a, b) => moment(b.created_at, 'DD-MM-YYYY HH:mm:ss') - moment(a.created_at, 'DD-MM-YYYY HH:mm:ss'));
                dispatch(slice.actions.setMediaImages({ results: sortedImages, count: response.data?.count }));
            } else {
                dispatch(setLoadMoreLoading(false));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(setLoadMoreLoading(false));
            dispatch(updateError(error || 'Something want wrong'));
        }
    };
}

export function deleteImage({ practiceId, id }) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/media/${id}`, 'delete');
            if (response.status === 204) {
                dispatch(getMediaImages({ practiceId, limit: 50, offset: 0, shouldReset: true }));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError(error || 'Something want wrong'));
        }
    };
}

export function pauseCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/pause/`, 'post');
            if (response.status === 200) {
                handleClose();
                dispatch(updateError({ success: true, message: 'Campaign paused successfully' }));
            } else {
                const msg = response.data?.message || response.data?.exception || JSON.stringify(response.data) || 'Something went wrong';
                const isTransitionError = msg.includes('TransitionNotAllowed') || msg.includes('PAUSED');
                dispatch(updateError({ success: isTransitionError, message: isTransitionError ? 'Campaign is already paused' : msg }));
                if (isTransitionError) handleClose();
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}


export function resumeCampaign(data, handleClose) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${data?.practiceId}/campaigns/${data.id}/resume/`, 'post');
            if (response.status === 200) {
                handleClose();
                dispatch(updateError({ success: true, message: 'Campaign resumed successfully' }));
            } else {
                const msg = response.data?.message || response.data?.exception || JSON.stringify(response.data) || 'Something went wrong';
                const isTransitionError = msg.includes('TransitionNotAllowed') || msg.includes('PAUSED');
                dispatch(updateError({ success: isTransitionError, message: isTransitionError ? 'Campaign is not paused' : msg }));
                if (isTransitionError) handleClose();
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getWhatsappTemplates(data) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Guard: skip API call if business account PK or template ID is missing
        if (!data?.practiceId || !data?.phone || !data?.templateId) return;
        try {
            dispatch(slice.actions.setPreviewWhatsappStart({ loading: true, template: {} }));
            const response = await callApi(
                `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/${data?.phone}/whatsapp-message-templates/${data?.templateId}`
            );
            dispatch(slice.actions.setPreviewWhatsappStart({ loading: false, template: response?.data }));
        } catch (err) {
            console.error("Failed to fetch whatsapp template", err);
            dispatch(slice.actions.setPreviewWhatsappStart({ loading: false, template: {} }));
        }
    }
}

export function getAllWhatsappTemplates(data) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Guard: skip API call if practiceId is missing
        if (!data?.practiceId) return;
        try {
            dispatch(slice.actions.setAllWhatsappTemplateLoading(true));
            const availableWhatsaAppBusinessAccResp = await callApi(
                `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/`,
                'GET'
            );
            const { count, results } = availableWhatsaAppBusinessAccResp?.data ?? {};
            if (count === 0) {
                dispatch(updateError({ success: "warning", message: "No Whatsapp Business Account set for this Practice" }));
                return;
            }
            const whatsappTemplates = await Promise.all(
                results?.map(async (businessAccount) => {
                    const { name, id, reviewStatus } = businessAccount ?? {};

                    await callApi(
                        `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/${id}/sync_templates/`,
                        'POST'
                    );

                    const phonesAvailable = await callApi(
                        `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/${id}/whatsapp-phones/`,
                        'GET'
                    );

                    const { display_phone_number: displayPhoneNumber, id: phoneId } = phonesAvailable?.data?.results?.[0] ?? {};

                    const templateResponse = await callApi(
                        `/api/v1/practices/${data?.practiceId}/whatsapp-business-accounts/${id}/whatsapp-message-templates/?&ordering=-created_at`
                    );

                    return templateResponse?.data?.results?.map((template) => ({
                        ...template,
                        businessName: name,
                        businessId: phoneId,
                        businessReviewStatus: reviewStatus,
                        displayPhoneNumber,
                    })) ?? [];
                })
            );

            // Flatten the nested arrays if needed
            const flattenedTemplates = whatsappTemplates.flat();
            dispatch(slice.actions?.setAllWhatsappTemplates(flattenedTemplates));
        } catch (error) {
            console.error("Failed to get all whatsapp templates", error);
            dispatch(updateError({ success: false, message: "Error in fetching whatsapp templates" }));
        } finally {
            dispatch(slice.actions.setAllWhatsappTemplateLoading(false));
        }
    }
}

export function getCampaignDetail({ practiceId, id }) {
    return async () => {
        try {
            dispatch(slice.actions.startCampaignDetailLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/campaigns/${id}/`, 'get');
            if (response.status === 200) {
                dispatch(slice.actions.setCampaignDetail(response.data));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to load campaign details' }));
                dispatch(slice.actions.setCampaignDetail(null));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            dispatch(slice.actions.setCampaignDetail(null));
        }
    };
}